// Vilken plattform sidan körs på. Webben och de två app-wrapparna behöver
// olika samtyckesflöden, behörighetsdialoger, länkhantering och köpregler,
// därför finns detta som ett eget litet lager i stället för spridda
// isAppMode()-kontroller.

import { useEffect, useState } from "react";
import { isAppMode, primeDeviceInfo } from "@/app-bridge/core/wrapper";

export type AppPlatform = "web" | "ios" | "android";

let cached: AppPlatform | null = null;

/** Gissar plattform direkt utan att vänta på appens enhetsinfo. */
function guessFromUserAgent(): AppPlatform {
  if (typeof navigator === "undefined") return "web";
  const ua = navigator.userAgent ?? "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  // iPadOS 13+ säger "Macintosh" men har touch.
  if (/macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return "ios";
  return "web";
}

/**
 * Plattform just nu. I webbläsare alltid "web" – även på en telefon – eftersom
 * det bara är inne i wrappern som de plattformsspecifika reglerna gäller.
 */
export function getPlatform(): AppPlatform {
  if (typeof window === "undefined") return "web";
  if (cached) return cached;
  if (!isAppMode()) return "web";
  cached = guessFromUserAgent();
  if (cached === "web") cached = "ios";
  return cached;
}

/** Sant bara i iOS- eller Android-appen. */
export function isNativeApp(): boolean {
  return getPlatform() !== "web";
}

/**
 * Läser plattform från appens enhetsinfo när den hunnit svara – mer pålitligt
 * än user agent. Anropas en gång vid start.
 */
export async function primePlatform(): Promise<AppPlatform> {
  const first = getPlatform();
  if (first === "web") return "web";
  const info = await primeDeviceInfo();
  const system = String(info?.system ?? "").toLowerCase();
  if (system.includes("android")) cached = "android";
  else if (system.includes("ios") || system.includes("ipad")) cached = "ios";
  return cached ?? first;
}

const PLATFORM_LABEL: Record<AppPlatform, string> = {
  web: "Webben",
  ios: "iOS-appen",
  android: "Android-appen",
};

export function platformLabel(platform: AppPlatform = getPlatform()): string {
  return PLATFORM_LABEL[platform];
}

/** Plattform i React. Uppdateras när appens enhetsinfo svarat. */
export function useAppPlatform(): AppPlatform {
  const [platform, setPlatform] = useState<AppPlatform>("web");
  useEffect(() => {
    setPlatform(getPlatform());
    let alive = true;
    void primePlatform().then((p) => {
      if (alive) setPlatform(p);
    });
    return () => {
      alive = false;
    };
  }, []);
  return platform;
}
